import type {
  DetailedCircuitFeature,
  DetailedCircuitGeometry,
  TrackContributionGpsCoord,
} from '../types/index.js';

function distanceSquared(left: TrackContributionGpsCoord, right: TrackContributionGpsCoord) {
  const metersPerDegreeLat = 111_320;
  const metersPerDegreeLon =
    metersPerDegreeLat * Math.cos(((left.lat + right.lat) * Math.PI) / 360);
  return (
    ((left.lon - right.lon) * metersPerDegreeLon) ** 2 +
    ((left.lat - right.lat) * metersPerDegreeLat) ** 2
  );
}

export function lineMidpoint(points: TrackContributionGpsCoord[] | undefined) {
  if (!points?.length) {
    return null;
  }
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lon: points.reduce((sum, point) => sum + point.lon, 0) / points.length,
  };
}

function nearestPointIndex(points: TrackContributionGpsCoord[], target: TrackContributionGpsCoord) {
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  points.forEach((point, index) => {
    const distance = distanceSquared(point, target);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });
  return bestIndex;
}

function centerlineSlice(
  centerLine: TrackContributionGpsCoord[],
  entry: TrackContributionGpsCoord,
  exit: TrackContributionGpsCoord,
) {
  const line =
    centerLine.length > 2 && distanceSquared(centerLine[0]!, centerLine.at(-1)!) < 0.25
      ? centerLine.slice(0, -1)
      : centerLine;
  if (line.length < 3) {
    return line;
  }
  const entryIndex = nearestPointIndex(line, entry);
  const exitIndex = nearestPointIndex(line, exit);
  const points: TrackContributionGpsCoord[] = [];
  let index = entryIndex;
  while (points.length <= line.length) {
    points.push(line[index]!);
    if (index === exitIndex) {
      break;
    }
    index = (index + 1) % line.length;
  }
  return points;
}

function curvatureScore(
  previous: TrackContributionGpsCoord,
  current: TrackContributionGpsCoord,
  next: TrackContributionGpsCoord,
) {
  const latScale = 111_320;
  const lonScale = latScale * Math.cos((current.lat * Math.PI) / 180);
  const incoming = {
    x: (current.lon - previous.lon) * lonScale,
    y: (current.lat - previous.lat) * latScale,
  };
  const outgoing = {
    x: (next.lon - current.lon) * lonScale,
    y: (next.lat - current.lat) * latScale,
  };
  const incomingLength = Math.hypot(incoming.x, incoming.y);
  const outgoingLength = Math.hypot(outgoing.x, outgoing.y);
  if (incomingLength < 0.1 || outgoingLength < 0.1) {
    return 0;
  }
  const cross = incoming.x * outgoing.y - incoming.y * outgoing.x;
  const dot = incoming.x * outgoing.x + incoming.y * outgoing.y;
  const angle = Math.abs(Math.atan2(cross, dot));
  return angle / ((incomingLength + outgoingLength) / 2);
}

export function deriveGeometricApexPoint(
  geometry: DetailedCircuitGeometry,
  feature: DetailedCircuitFeature,
) {
  if (feature.apexPoint) {
    return feature.apexPoint;
  }
  const entry = lineMidpoint(feature.entryLine);
  const exit = lineMidpoint(feature.exitLine);
  const centerLine = geometry.centerLine ?? [];
  if (!entry || !exit || centerLine.length < 3) {
    return lineMidpoint(feature.polygonRings?.[0]) ?? feature.directionalPoint ?? null;
  }

  const cornerLine = centerlineSlice(centerLine, entry, exit);
  if (cornerLine.length < 5) {
    return cornerLine[Math.floor(cornerLine.length / 2)] ?? null;
  }
  const offset = Math.max(1, Math.min(3, Math.floor(cornerLine.length / 8)));
  const candidates: Array<{ index: number; score: number }> = [];
  for (let index = offset; index < cornerLine.length - offset; index += 1) {
    candidates.push({
      index,
      score: curvatureScore(
        cornerLine[index - offset]!,
        cornerLine[index]!,
        cornerLine[index + offset]!,
      ),
    });
  }
  candidates.sort((left, right) => right.score - left.score);
  const positiveScores = candidates
    .map((candidate) => candidate.score)
    .filter((score) => score > 0);
  const medianScore = positiveScores.length
    ? positiveScores[Math.floor(positiveScores.length / 2)]!
    : 0;
  const strongest = candidates[0];
  const nearlyConstantRadius =
    !strongest || strongest.score <= 0 || (medianScore > 0 && strongest.score < medianScore * 1.25);
  const apexIndex = nearlyConstantRadius ? Math.floor(cornerLine.length / 2) : strongest.index;
  return cornerLine[apexIndex] ?? null;
}

export function cornerApexSuggestionsFromDetailedGeometry(geometry: DetailedCircuitGeometry) {
  const enriched = withDerivedGeometricApexes(geometry);
  return enriched.features
    .filter((feature) => feature.type.toLowerCase() === 'corner')
    .map((feature, index) => {
      const apex =
        feature.apexPoint ??
        lineMidpoint(feature.polygonRings?.[0]) ??
        lineMidpoint(feature.entryLine) ??
        lineMidpoint(feature.exitLine);
      if (!apex) {
        return null;
      }

      const turnNumber = Number(feature.label.match(/\d+/)?.[0] ?? index + 1);
      return {
        id: feature.id,
        label: feature.label,
        lat: apex.lat,
        lon: apex.lon,
        turnNumber,
        confidence: 0.85,
      };
    })
    .filter(
      (
        corner,
      ): corner is {
        id: string;
        label: string;
        lat: number;
        lon: number;
        turnNumber: number;
        confidence: number;
      } => corner !== null,
    )
    .sort((left, right) => left.turnNumber - right.turnNumber);
}

export function withDerivedGeometricApexes(geometry: DetailedCircuitGeometry) {
  const enriched: DetailedCircuitGeometry = {
    ...geometry,
    features: geometry.features.map((feature) =>
      feature.type.toLowerCase() === 'corner'
        ? { ...feature, apexPoint: deriveGeometricApexPoint(geometry, feature) }
        : feature,
    ),
  };
  return enriched;
}

export function startLineFromDetailedGeometry(geometry: DetailedCircuitGeometry | undefined) {
  const feature = geometry?.features.find((candidate) => {
    const type = candidate.type.toLowerCase().replace(/[^a-z]/g, '');
    return type === 'startfinish' || type === 'startline';
  });
  const points =
    feature?.entryLine?.length === 2
      ? feature.entryLine
      : feature?.exitLine?.length === 2
        ? feature.exitLine
        : null;
  return points ? { pointA: points[0]!, pointB: points[1]!, confidence: 0.8 } : null;
}
